
import React, { useState, useEffect, useRef, useMemo } from "react";
import { FaSearch, FaClock, FaBolt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../Style/Dashboard.css";
import { useQuery } from "@tanstack/react-query";
import { PubSub, Auth } from "aws-amplify";
import carGreenSvg from "../assets/car-green.svg";
import carYellowSvg from "../assets/car-yellow.svg";
import carRedSvg from "../assets/car-red.svg";
import carBlackSvg from "../assets/car-black.svg";
import carBlueSvg from "../assets/car-blue.svg";

const API_BASE_URL = "https://api.yellowmatrix.co.in";

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Custom vehicle icon with rotation logic
const createVehicleIcon = (status, bearing) => {
  const iconUrl = vehicleIcons[status] || vehicleIcons.UNKNOWN;
  return L.divIcon({
    className: `vehicle-icon ${status.toLowerCase().replace(" ", "-")}`,
    iconSize: [37, 37],
    iconAnchor: [16, 16],
    status,
    bearing,
    html: `
      <div style="position: relative;">
        <img
          src="${iconUrl}"
          style="
            width: 100%;
            height: 100%;
            transform: rotate(${bearing}deg);
            transform-origin: center center;
            transition: transform 0.7s ease-in-out;
          "
        />
      </div>
    `,
  });
};

const vehicleIcons = {
  RUNNING: carGreenSvg,
  IDLE: carYellowSvg,
  HALT: carRedSvg,
  "NO GPS": carBlackSvg,
  OFFLINE: carBlueSvg,
  UNKNOWN: carBlueSvg,
};

const getVehicleStatus = ({ Speed, Ignition, GPSFix, Latitude, Longitude }) => {
  if (GPSFix === 0 || Latitude === 0 || Longitude === 0) return "NO GPS";
  if (Speed > 3 && Ignition === 1) return "RUNNING";
  if (Ignition === 1 && Speed <= 3) return "IDLE";
  if (Ignition === 0) return "HALT";
  return "UNKNOWN";
};

const calculateBearing = (from, to) => {
  if (!from || !to) return to?.Heading || 0;
  const φ1 = (from.Latitude * Math.PI) / 180;
  const φ2 = (to.Latitude * Math.PI) / 180;
  const Δλ = ((to.Longitude - from.Longitude) * Math.PI) / 180;

  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x =
    Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  let θ = Math.atan2(y, x);
  return ((θ * 180) / Math.PI + 360) % 360;
};

const haversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const defaultMapCenter = [19.253088, 72.868688];
const defaultMapZoom = 10;
const MIN_MOVEMENT_DISTANCE = 10;

const formatRelativeTime = (timestamp) => {
  if (!timestamp) return "No data";

  const now = new Date();
  const date = new Date(timestamp);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  const MINUTE = 60;
  const HOUR = MINUTE * 60;
  const DAY = HOUR * 24;
  const WEEK = DAY * 7;
  const MONTH = DAY * 30.44;
  const YEAR = DAY * 365.25;

  if (seconds < 5) return "just now";
  if (seconds < MINUTE) return `${seconds} seconds ago`;
  if (seconds < HOUR) {
    const minutes = Math.floor(seconds / MINUTE);
    return minutes === 1 ? "a minute ago" : `${minutes} minutes ago`;
  }
  if (seconds < DAY) {
    const hours = Math.floor(seconds / HOUR);
    return hours === 1 ? "an hour ago" : `${hours} hours ago`;
  }
  if (seconds < WEEK) {
    const days = Math.floor(seconds / DAY);
    return days === 1 ? "1 day ago" : `${days} days ago`;
  }
  if (seconds < MONTH) {
    const weeks = Math.floor(seconds / WEEK);
    return weeks === 1 ? "a week ago" : `${weeks} weeks ago`;
  }
  if (seconds < YEAR) {
    const months = Math.floor(seconds / MONTH);
    return months === 1 ? "a month ago" : `${months} months ago`;
  }

  const years = Math.floor(seconds / YEAR);
  return years === 1 ? "a year ago" : `${years} years ago`;
};

const StatusCard = ({ label, value, id, isActive, onClick }) => (
  <div
    className={`tooltip-container card ${isActive ? "active" : ""}`}
    id={id}
    onClick={onClick}
    style={{ cursor: "pointer" }}
  >
    {label}: {value}
    <span className="tooltip-text">
      {label === "RUNNING" && "Vehicles running at more than 3 kmph"}
      {label === "IDLE" && "Vehicles with ignition ON and not moving"}
      {label === "HALT" &&
        "Vehicles with ignition OFF and sent data in last 30 minutes"}
      {label === "NO GPS" &&
        "Vehicles that have sent data in last 30 minutes without a valid GPS data"}
      {label === "OFFLINE" &&
        "Vehicles which haven't sent data in last 30 minutes"}
      {label === "NO DATA" &&
        "Vehicles which haven't sent valid data since installation"}
      {label === "DISCONNECTED" &&
        "Offline vehicles which sent the last packet on the device secondary battery"}
    </span>
  </div>
);

export default function Dashboard() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedVehicles, setSelectedVehicles] = useState(new Set());
  const [visibleCount, setVisibleCount] = useState(7);
  const [followVehicleId] = useState(null);
  const [isManualView, setIsManualView] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const vehicleListRef = useRef(null);
  const token = localStorage.getItem("token");
  const [topic, setTopic] = useState(null);
  const [liveVehicleData, setLiveVehicleData] = useState({});
  const vehicleHistory = useRef({});
  const hasInitialZoomed = useRef(false);
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState("ALL");

  const fetchVehicles = async () => {
    if (!token)
      throw new Error("Authentication token is missing. Please log in.");

    try {
      const response = await fetch(`${API_BASE_URL}/vehicles`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        localStorage.removeItem("token");
        throw new Error("Session expired. Please log in again.");
      }

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      return data.sort((a, b) => new Date(b.LastTime) - new Date(a.LastTime));
    } catch (error) {
      console.error("Fetch vehicles error:", error);
      throw error;
    }
  };

  const {
    data: staticVehicles = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["vehicles", token],
    queryFn: fetchVehicles,
    enabled: !!token,
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 2,
    refetchOnWindowFocus: true,
    onError: (err) => console.error("Error fetching vehicle data:", err),
    onSuccess: (data) => {
      if (data && data.length > 0) {
        setSelectedVehicles((prev) => {
          const newSet = new Set(prev);
          data.forEach((v) => newSet.add(v.VehicleID));
          return newSet;
        });
      }
    },
  });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/vehiclelivedata`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Request failed");
        }

        const config = await response.json();
        setTopic(config.topic);
      } catch (err) {
        console.error("Error fetching config:", err.message);
      }
    };

    if (token) fetchConfig();
  }, [token]);

  useEffect(() => {
    if (!topic) return;

    let subscription;

    const subscribeToTopic = async () => {
      try {
        await Auth.currentCredentials();
        subscription = PubSub.subscribe(topic).subscribe({
          next: (data) => {
            const payload = data.value;
            const pvtString = payload?.PVT;
            if (!pvtString || !pvtString.startsWith("$,PVT")) return;

            const parts = pvtString.split(",");
            if (parts.length < 30) return;

            const convertDMM = (val, dir) => {
              if (isNaN(val)) return 0;
              const deg = Math.floor(val / 100);
              const min = val - deg * 100;
              const dec = deg + min / 60;
              return dir === "S" || dir === "W" ? -dec : dec;
            };

            const parsed = {
              VehicleID: parts[7],
              GPSFix: parseInt(parts[8]),
              Latitude: convertDMM(parseFloat(parts[11]), parts[12]),
              Longitude: convertDMM(parseFloat(parts[13]), parts[14]),
              Speed: parseFloat(parts[15]),
              Ignition: parseInt(parts[22]),
              Heading: parts.length > 23 ? parseFloat(parts[23]) : 0,
              timestamp: Date.now(),           
            };

            const vehicleID = parsed.VehicleID;
            let bearing = parsed.Heading;
            const prev = vehicleHistory.current[vehicleID];
            if (prev) {
              const dist = haversineDistance(
                prev.Latitude,
                prev.Longitude,
                parsed.Latitude,
                parsed.Longitude,
              );
              bearing =
                dist > MIN_MOVEMENT_DISTANCE
                  ? calculateBearing(prev, parsed)
                  : prev.bearing || parsed.Heading;
            }
            parsed.bearing = bearing;
            vehicleHistory.current = {
              ...vehicleHistory.current,
              [vehicleID]: parsed,
            };

            setLiveVehicleData((prev) => ({ ...prev, [vehicleID]: parsed }));
          },
          error: (err) => console.error("Subscription error:", err),
        });
      } catch (err) {
        console.error("Initial subscription error:", err);
      }
    };

    subscribeToTopic();

    return () => {
      if (subscription) subscription.unsubscribe();
    };
  }, [topic]);

  const mergedVehicles = useMemo(() => {
    if (staticVehicles.length === 0) return [];

    return staticVehicles.map((staticVehicle) => {
      const liveVehicle = liveVehicleData[staticVehicle.VehicleID];
      const now = Date.now();
      const isLive = liveVehicle && now - liveVehicle.timestamp < 10000;

      if (isLive) {
        return {
          ...staticVehicle,
          Location: {
            Latitude: liveVehicle.Latitude,
            Longitude: liveVehicle.Longitude,
          },
          Speed: liveVehicle.Speed,
          Ignition: liveVehicle.Ignition,
          GPSFix: liveVehicle.GPSFix,
          LastTime: liveVehicle.timestamp,
          isLive: true,
          bearing: liveVehicle.bearing,
          liveStatus: getVehicleStatus(liveVehicle),
          // 🎯 ONLY INTEGRATED CHANGE: Explicitly preserve backend dynamic updates here
          Utilization: staticVehicle.Utilization != null ? staticVehicle.Utilization : 0,
        };
      } else {
        return { 
          ...staticVehicle, 
          isLive: false, 
          liveStatus: "OFFLINE",
          // 🎯 ONLY INTEGRATED CHANGE: Explicitly preserve backend dynamic updates here
          Utilization: staticVehicle.Utilization != null ? staticVehicle.Utilization : 0,
        };
      }
    });
  }, [staticVehicles, liveVehicleData]);

  // Filter vehicles based on search term AND selected status
  const filteredVehicles = useMemo(() => {
    let filtered = mergedVehicles;

    // First filter by search term
    if (searchTerm !== "") {
      filtered = filtered.filter((v) =>
        v.VehicleID.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Then filter by selected status if any
    if (selectedStatus) {
      filtered = filtered.filter((v) => {
        const status = v.liveStatus || getVehicleStatus(v);
        return status === selectedStatus;
      });
    }

    if (selectedTab !== "ALL") {
      filtered = filtered.filter((v) => {
        const vehicleType = (v.VehicleType || "").trim().toUpperCase();
        const powerTrain = (v.Powertrain || "").trim().toUpperCase();

        if (selectedTab === "OTHER") {
          return (
            vehicleType !== "2W" &&
            vehicleType !== "3W" &&
            vehicleType !== "4W" &&
            powerTrain !== "ELECTRIC"
          );
        }

        return vehicleType === selectedTab || powerTrain === selectedTab;
      });
    }

    return filtered;
  }, [searchTerm, mergedVehicles, selectedStatus, selectedTab]);

  useEffect(() => {
    setVisibleCount(7);
    if (vehicleListRef.current) {
      vehicleListRef.current.scrollTop = 0;
    }
  }, [searchTerm, selectedStatus]);

  // Map initialization with interaction tracking
  useEffect(() => {
    if (!mapRef.current && typeof window !== "undefined") {
      mapRef.current = L.map("map").setView(defaultMapCenter, defaultMapZoom);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
      }).addTo(mapRef.current);

      // Track manual zoom and pan events
      const handleManualInteraction = () => {
        setIsManualView(true);
      };

      mapRef.current.on("zoomstart", handleManualInteraction);
      mapRef.current.on("dragstart", handleManualInteraction);

      window.persistedMap = mapRef.current;
    } else if (window.persistedMap) {
      mapRef.current = window.persistedMap;
    }

    return () => {
      Object.values(markersRef.current).forEach((marker) => {
        if (mapRef.current && marker) {
          mapRef.current.removeLayer(marker);
        }
      });
      markersRef.current = {};
    };
  }, []);

  // Marker Manager - Only update markers that need updating (NO FLICKERING)
  useEffect(() => {
    if (!mapRef.current || mergedVehicles.length === 0) return;

    const currentMarkers = markersRef.current;
    const newMarkers = {};

    // Process vehicles to show based on selection
    const vehiclesToShow =
      selectedVehicles.size > 0
        ? mergedVehicles.filter((v) => selectedVehicles.has(v.VehicleID))
        : mergedVehicles;

    // First pass: update existing markers or create new ones
    vehiclesToShow.forEach((vehicle) => {
      if (!vehicle?.Location?.Latitude || !vehicle?.Location?.Longitude) return;

      const status = vehicle.liveStatus || getVehicleStatus(vehicle);
      const bearing = vehicle.bearing || 0;
      const vehicleId = vehicle.VehicleID;

      // If marker exists, update its position and icon if needed
      if (currentMarkers[vehicleId]) {
        const marker = currentMarkers[vehicleId];
        const oldLatLng = marker.getLatLng();
        const newLatLng = L.latLng(
          vehicle.Location.Latitude,
          vehicle.Location.Longitude,
        );

        // Only update if position changed significantly (more than 5 meters)
        if (oldLatLng.distanceTo(newLatLng) > 5) {
          marker.setLatLng(newLatLng);
        }

        // Update icon if status or bearing changed significantly
        const currentIcon = marker.options.icon;
        if (
          !currentIcon ||
          currentIcon.options.status !== status ||
          Math.abs((currentIcon.options.bearing || 0) - bearing) > 5
        ) {
          marker.setIcon(createVehicleIcon(status, bearing));
        }

        newMarkers[vehicleId] = marker;
        delete currentMarkers[vehicleId]; // Remove from current markers
      } else {
        // Create new marker
        const icon = createVehicleIcon(status, bearing);
        const marker = L.marker(
          [vehicle.Location.Latitude, vehicle.Location.Longitude],
          { icon },
        )
          .bindPopup(
            `<b>${vehicle.VehicleID}</b><br>Status: ${status}<br>Lat: ${
              vehicle.Location.Latitude
            }, Lng: ${
              vehicle.Location.Longitude
            }<br>Last Update: ${formatRelativeTime(vehicle.LastTime)}`,
          )
          .addTo(mapRef.current);

        newMarkers[vehicleId] = marker;
      }
    });

    // Remove markers that are no longer needed (not in current selection or no longer exist)
    Object.keys(currentMarkers).forEach((vehicleId) => {
      if (mapRef.current && currentMarkers[vehicleId]) {
        mapRef.current.removeLayer(currentMarkers[vehicleId]);
      }
    });

    markersRef.current = newMarkers;
  }, [mergedVehicles, selectedVehicles]);

  // Auto-Zoomer - Only zoom on selection changes, NEVER on live data
  useEffect(() => {
    if (!mapRef.current || followVehicleId || isManualView) return;

    const vehiclesToShow =
      selectedVehicles.size > 0
        ? mergedVehicles.filter((v) => selectedVehicles.has(v.VehicleID))
        : mergedVehicles;

    const boundsToFit = vehiclesToShow
      .filter((v) => v?.Location?.Latitude && v?.Location?.Longitude)
      .map((v) => L.latLng(v.Location.Latitude, v.Location.Longitude));

    if (boundsToFit.length === 0) {
      mapRef.current.setView(defaultMapCenter, defaultMapZoom);
      return;
    }

    if (selectedVehicles.size === 1 && boundsToFit.length === 1) {
      mapRef.current.setView(boundsToFit[0], 18);
    } else {
      mapRef.current.fitBounds(L.latLngBounds(boundsToFit), {
        padding: [50, 50],
        maxZoom: 16,
      });
    }

    // Reset manual view flag after auto-zooming
    setIsManualView(false);
  }, [selectedVehicles, followVehicleId, mergedVehicles, isManualView]);

  // Reset manual view when selection is cleared
  useEffect(() => {
    setIsManualView(false);
  }, [selectedVehicles]);

  // Follow Cam
  useEffect(() => {
    if (!followVehicleId || !mapRef.current) return;

    const vehicle = mergedVehicles.find((v) => v.VehicleID === followVehicleId);
    if (!vehicle?.Location?.Latitude || !vehicle?.Location?.Longitude) return;

    mapRef.current.setView(
      [vehicle.Location.Latitude, vehicle.Location.Longitude],
      18,
      { animate: true, duration: 0.5 },
    );

    // Reset manual view when following a vehicle
    setIsManualView(false);
  }, [mergedVehicles, followVehicleId]);

  // Initial zoom to fit all vehicles
  useEffect(() => {
    if (
      !mapRef.current ||
      mergedVehicles.length === 0 ||
      hasInitialZoomed.current
    )
      return;

    const bounds = mergedVehicles
      .filter((v) => v?.Location?.Latitude && v?.Location?.Longitude)
      .map((v) => L.latLng(v.Location.Latitude, v.Location.Longitude));

    if (bounds.length > 0) {
      mapRef.current.fitBounds(L.latLngBounds(bounds), {
        padding: [50, 50],
        maxZoom: 16,
      });
      hasInitialZoomed.current = true;
    }
  }, [mergedVehicles]);

  const toggleVehicleOnMap = (vehicle) => {
    const updatedSelectedVehicles = new Set(selectedVehicles);
    if (updatedSelectedVehicles.has(vehicle.VehicleID)) {
      updatedSelectedVehicles.delete(vehicle.VehicleID);
    } else {
      updatedSelectedVehicles.add(vehicle.VehicleID);
    }
    setSelectedVehicles(updatedSelectedVehicles);
  };

  const handleStatusClick = (status) => {
    // Toggle selection if the same status is clicked again
    if (selectedStatus === status) {
      setSelectedStatus(null);
    } else {
      setSelectedStatus(status);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 1) {
      setVisibleCount((prev) => Math.min(prev + 7, filteredVehicles.length));
    }
  };

  const statusCounts = useMemo(() => {
    const counts = { RUNNING: 0, IDLE: 0, HALT: 0, "NO GPS": 0, OFFLINE: 0 };
    mergedVehicles.forEach((v) => {
      const status = v.liveStatus || getVehicleStatus(v);
      if (counts[status] !== undefined) {
        counts[status]++;
      }
    });
    return counts;
  }, [mergedVehicles]);

  const totalVehicles = mergedVehicles.length;

  return (
    <div className="main-content">
      <h2 id="Dashboard-H2">Dashboard</h2>
      <hr className="line-Dashbord" width="100%" />

      <div className="dashboard-container">
        <div className="tracking-section">
          <h4 style={{ color: "#478cd6" }}>
            Tracking: {totalVehicles - statusCounts.OFFLINE}
          </h4>
          <div className="dashboard-cards">
            <StatusCard
              label="RUNNING"
              value={statusCounts.RUNNING}
              id="green"
              isActive={selectedStatus === "RUNNING"}
              onClick={() => handleStatusClick("RUNNING")}
            />
            <StatusCard
              label="IDLE"
              value={statusCounts.IDLE}
              id="yellow"
              isActive={selectedStatus === "IDLE"}
              onClick={() => handleStatusClick("IDLE")}
            />
            <StatusCard
              label="HALT"
              value={statusCounts.HALT}
              id="red"
              isActive={selectedStatus === "HALT"}
              onClick={() => handleStatusClick("HALT")}
            />
            <StatusCard
              label="NO GPS"
              value={statusCounts["NO GPS"]}
              id="gray"
              isActive={selectedStatus === "NO GPS"}
              onClick={() => handleStatusClick("NO GPS")}
            />
          </div>
        </div>
        <div className="non-tracking-section">
          <h4 style={{ color: "#555" }}>
            Non Tracking: {statusCounts.OFFLINE}
          </h4>
          <div className="dashboard-cards">
            <StatusCard
              label="OFFLINE"
              value={statusCounts.OFFLINE}
              id="dark-blue"
              isActive={selectedStatus === "OFFLINE"}
              onClick={() => handleStatusClick("OFFLINE")}
            />
            <div
              className={`tooltip-container card ${
                selectedStatus === "NO DATA" ? "active" : ""
              }`}
              id="gray"
              onClick={() => handleStatusClick("NO DATA")}
              style={{ cursor: "pointer" }}
            >
              NO DATA: 0
              <span className="tooltip-text">
                Vehicles which haven't sent valid data since installation
              </span>
            </div>
            <div
              className={`tooltip-container card ${
                selectedStatus === "DISCONNECTED" ? "active" : ""
              }`}
              id="gray"
              onClick={() => handleStatusClick("DISCONNECTED")}
              style={{ cursor: "pointer" }}
            >
              DISCONNECTED: 0
              <span className="tooltip-text">
                Offline vehicles which sent the last packet on the device
                secondary battery
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="vehicle-container">
        <div className="left-section">
          <div className="vehicle-tabs-container">
            {["2W", "3W", "4W", "ELECTRIC", "OTHER"].map((tab) => (
              <button
                key={tab}
                className={`vehicle-tab-button ${
                  selectedTab === tab ? "vehicle-tab-active" : ""
                }`}
                onClick={() => setSelectedTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="search-container">
            <div className="search-icon">
              <FaSearch />
            </div>
            <input
              type="text"
              id="search"
              placeholder="Search Tracking Vehicles"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div
            className="vehicle-list"
            ref={vehicleListRef}
            onScroll={handleScroll}
            style={{ overflowY: "auto" }}
          >
            {isLoading && (
              <div className="loading-message">Loading vehicles...</div>
            )}
            {isError && (
              <div className="error-message">
                Error: {error?.message || "Failed to fetch vehicles."}
              </div>
            )}

            {!isLoading &&
              !isError &&
              filteredVehicles.slice(0, visibleCount).map((vehicle) => (
                <div
                  key={vehicle.VehicleID}
                  className="vehicle"
                  onClick={() => toggleVehicleOnMap(vehicle)}
                  onDoubleClick={() =>
                    navigate(`/vehicle/${vehicle.VehicleID}`)
                  }
                >
                  <input
                    type="checkbox"
                    checked={selectedVehicles.has(vehicle.VehicleID)}
                    readOnly
                  />
                  <div className="vehicle-info">
                    <div className="vehicle-info-top-row">
                      <p className="vehicle-number">{vehicle.VehicleID}</p>
                      <p
                        className={
                          vehicle.VehicleType && vehicle.Powertrain
                            ? "vehicle-type"
                            : ""
                        }
                      >
                        <span className="vehicle-origin">
                          {vehicle.VehicleType || ""}
                        </span>
                        {vehicle.Powertrain && vehicle.VehicleType && " , "}
                        <span className="vehicle-powertrain">
                          {vehicle.Powertrain || ""}
                        </span>
                      </p>

                      <p className="customer-id">
                        {vehicle.CustomerID || "N/A"}
                      </p>
                    </div>
                    <div className="vehicle-time-row">
                      <p className="vehicle-time">
                        {vehicle.isLive
                          ? "now"
                          : formatRelativeTime(vehicle.LastTime)}
                      </p>

                      <p className="customer-metrics">
                        <span
                          className="vehicle-efficiency"
                          title="Utilization"
                        >
                          <FaClock style={{ marginRight: "4px", opacity: 0.6 }} />
                          {vehicle.Utilization != null
                            ? `${vehicle.Utilization}%`
                            : "0%"}
                        </span>

                        {" | "}

                        <span
                          className="vehicle-performance"
                          title="Efficiency"
                        >
                          <FaBolt style={{ marginRight: "4px", opacity: 0.6 }} />
                          {vehicle.Efficiency != null
                            ? `${vehicle.Efficiency}%`
                            : "N/A"}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            {visibleCount < filteredVehicles.length && (
              <div className="load-more">Scroll down to load more vehicles</div>
            )}
            {!isLoading && !isError && filteredVehicles.length === 0 && (
              <div className="no-results">No vehicles found.</div>
            )}
          </div>
        </div>

        <div className="map-container">
          <div id="map" style={{ height: "500px", width: "100%" }}></div>
        </div>
      </div>
    </div>
  );
}